import { memo, useMemo, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Document, Page } from "react-pdf";
import { cn } from "@/lib/utils";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import {
  FILE_PREVIEW_DEFAULT_MAX_WIDTH,
  FILE_PREVIEW_DEFAULT_MAX_HEIGHT,
  FILE_PREVIEW_MIN_HEIGHT,
} from "@/constants";
import { FilePreviewLoadingState } from "./FilePreviewLoadingState";
import { FilePreviewErrorState } from "./FilePreviewErrorState";
import { FilePreviewPdfIframePreview } from "./FilePreviewPdfIframePreview";
import { FilePreviewPdfPagination } from "./FilePreviewPdfPagination";
import { useContainerWidth, usePdfDocument } from "./hooks";

interface FilePreviewPdfPreviewProps {
  src: string;
  maxWidth?: number;
  maxHeight?: number;
  isFullScreen?: boolean;
  isScrollLayout?: boolean;
}

export const FilePreviewPdfPreview = memo(
  ({
    src,
    maxWidth = FILE_PREVIEW_DEFAULT_MAX_WIDTH,
    maxHeight = FILE_PREVIEW_DEFAULT_MAX_HEIGHT,
    isFullScreen = false,
    isScrollLayout = false,
  }: FilePreviewPdfPreviewProps) => {
    const { t } = useTranslation();
    const { containerRef, width: responsiveWidth } = useContainerWidth();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const {
      numPages,
      pageNumber,
      setPageNumber,
      loading,
      error,
      onDocumentLoadSuccess,
      onDocumentLoadError,
    } = usePdfDocument(src);

    const isCrossOrigin = useMemo(() => {
      try {
        if (src.startsWith("data:")) return false;
        const urlObj = new URL(src);
        return urlObj.origin !== window.location.origin;
      } catch {
        return false;
      }
    }, [src]);

    const pdfWidth = responsiveWidth || maxWidth;

    const containerClassName = useMemo(() => {
      const baseClasses = "px-0 md:px-8 flex flex-col w-full";
      if (isFullScreen) return `h-full ${baseClasses}`;
      return `items-center ${baseClasses}`;
    }, [isFullScreen]);

    const renderDocument = useMemo(
      () => (
        <Document
          file={src}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
        >
          <Page
            pageNumber={pageNumber}
            width={pdfWidth}
            renderTextLayer={true}
            renderAnnotationLayer={true}
          />
        </Document>
      ),
      [src, onDocumentLoadSuccess, onDocumentLoadError, pageNumber, pdfWidth],
    );

    const scrollToTop = useCallback(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, []);

    useEffect(() => {
      if (pageNumber > 0 && isScrollLayout) {
        scrollToTop();
      }
    }, [pageNumber, isScrollLayout, scrollToTop]);

    const handlePreviousPage = useCallback(
      () => setPageNumber((p) => Math.max(1, p - 1)),
      [setPageNumber],
    );

    const handleNextPage = useCallback(
      () => setPageNumber((p) => Math.min(numPages || 1, p + 1)),
      [setPageNumber, numPages],
    );

    const errorStyle = useMemo(() => {
      if (isFullScreen) return undefined;
      const style: React.CSSProperties = {};
      if (maxWidth) style.width = `${maxWidth}px`;
      if (maxHeight) style.height = `${maxHeight}px`;
      return Object.keys(style).length > 0 ? style : undefined;
    }, [isFullScreen, maxWidth, maxHeight]);

    const loadingStyle = useMemo(() => {
      if (isFullScreen) {
        return {
          minHeight: `${FILE_PREVIEW_MIN_HEIGHT}px`,
          width: "100%",
          height: "100%",
        };
      }
      const style: React.CSSProperties = {};
      if (maxWidth) style.width = `${maxWidth}px`;
      if (maxHeight) style.height = `${maxHeight}px`;
      return Object.keys(style).length > 0 ? style : undefined;
    }, [isFullScreen, maxWidth, maxHeight]);

    if (isCrossOrigin) {
      return (
        <FilePreviewPdfIframePreview
          src={src}
          isFullScreen={isFullScreen}
          maxWidth={maxWidth}
          maxHeight={maxHeight}
        />
      );
    }

    if (error) {
      return (
        <FilePreviewErrorState
          title={t("filePreview.errorLoadingPdf")}
          message={error}
          isFullScreen={isFullScreen}
          style={errorStyle}
        />
      );
    }

    return (
      <div
        ref={isScrollLayout ? undefined : containerRef}
        className={containerClassName}
      >
        {isScrollLayout ? (
          <>
            <div
              ref={scrollContainerRef}
              className="flex-1 overflow-y-auto [scrollbar-gutter:stable] flex justify-center relative"
            >
              {/* Measures the width left for the page: without the container padding and the scrollbar. */}
              <div
                ref={containerRef}
                aria-hidden
                className="absolute inset-x-0 top-0 h-0"
              />
              {loading && (
                <div className="absolute inset-0 bg-gray-100 rounded-lg flex items-center justify-center z-10">
                  <FilePreviewLoadingState
                    message={t("filePreview.loadingPdf")}
                    isFullScreen={isFullScreen}
                  />
                </div>
              )}
              <div className={cn(loading ? "opacity-0" : "opacity-100")}>
                {renderDocument}
              </div>
            </div>
            {!loading && numPages && numPages > 1 && (
              <FilePreviewPdfPagination
                pageNumber={pageNumber}
                numPages={numPages}
                onPrevious={handlePreviousPage}
                onNext={handleNextPage}
                className="sticky bottom-0"
              />
            )}
          </>
        ) : (
          <>
            <div
              className={cn(
                "flex-1 min-h-0 overflow-auto",
                isFullScreen && "flex flex-col space-y-4",
              )}
            >
              {loading && (
                <div
                  className={cn(
                    isFullScreen &&
                      "flex-1 flex items-center justify-center min-h-0 w-full",
                  )}
                >
                  <FilePreviewLoadingState
                    message={t("filePreview.loadingPdf")}
                    isFullScreen={isFullScreen}
                    style={loadingStyle}
                  />
                </div>
              )}
              <div className={cn(loading ? "hidden" : "flex justify-center")}>
                {renderDocument}
              </div>
            </div>
            {!loading && numPages && numPages > 1 && (
              <FilePreviewPdfPagination
                pageNumber={pageNumber}
                numPages={numPages}
                onPrevious={handlePreviousPage}
                onNext={handleNextPage}
                className={cn("sticky bottom-0 z-10", isFullScreen && "pt-4")}
              />
            )}
          </>
        )}
      </div>
    );
  },
);
